<template>
  <div class="report-page">
    <div class="page-header">
      <h2>지출결의서 레포트</h2>
      <el-button size="small" @click="helpVisible = true">도움말</el-button>
      <HelpPanel v-model="helpVisible" :content-path="'screens/expense-report'" />
      <p class="page-sub">기간·차원별 지출 집계와 드릴다운 분석</p>
    </div>

    <!-- 필터/피벗 빌더 -->
    <div class="toolbar card">
      <el-date-picker
        v-model="dateRange"
        type="daterange"
        range-separator="~"
        start-placeholder="시작일"
        end-placeholder="종료일"
        value-format="YYYY-MM-DD"
        style="width: 300px"
      />

      <el-select v-model="groupBy" multiple collapse-tags placeholder="그룹 기준" style="width: 260px">
        <el-option label="년도" value="year" />
        <el-option label="월" value="month" />
        <el-option label="일" value="day" />
        <el-option label="부서" value="department" />
        <el-option label="거래처" value="partner" />
        <el-option label="작성자" value="author" />
        <el-option label="상태" value="status" />
        <el-option label="과세구분" value="vat_type" />
      </el-select>

      <el-select v-model="metric" placeholder="집계" style="width: 110px">
        <el-option label="합계" value="sum" />
        <el-option label="평균" value="avg" />
        <el-option label="최대" value="max" />
        <el-option label="최소" value="min" />
        <el-option label="건수" value="count" />
      </el-select>

      <el-select v-model="chartType" placeholder="차트" style="width: 110px">
        <el-option label="막대" value="bar" />
        <el-option label="선" value="line" />
      </el-select>

      <el-button type="primary" :loading="loading" @click="fetchPivot">조회</el-button>
    </div>

    <div class="grid-chart-container">
      <section class="panel card">
        <header class="panel-head">
          <span class="panel-title">집계 표</span>
          <span class="panel-hint">행 클릭 → 상세 드릴다운</span>
        </header>
        <ag-grid-vue
          class="ag-theme-alpine report-grid"
          :rowData="rowData"
          :columnDefs="columnDefs"
          :defaultColDef="defaultColDef"
          @row-clicked="onRowClicked"
        />
      </section>

      <section class="panel card">
        <header class="panel-head">
          <span class="panel-title">차트</span>
        </header>
        <div class="chart-box">
          <canvas ref="canvasRef"></canvas>
        </div>
      </section>
    </div>

    <!-- 드릴다운 상세 -->
    <el-dialog v-model="showDetail" :title="detailTitle" width="90%" top="5vh">
      <div class="detail-summary">
        총 {{ detailCount }}건 · 합계 {{ formatInt(detailTotal) }}
      </div>
      <div v-loading="detailLoading">
        <ag-grid-vue
          class="ag-theme-alpine"
          :rowData="detailRows"
          :columnDefs="detailColumns"
          :defaultColDef="defaultColDef"
          style="height: 60vh; width: 100%;"
        />
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import HelpPanel from '@/components/help/HelpPanel.vue'
import { ref as _helpRef } from 'vue'
const helpVisible = _helpRef(false)
import { ref, shallowRef, onMounted, onBeforeUnmount } from "vue";
import axiosInstance from "@/api/axios";
import { ElMessage } from "element-plus";

import {
  Chart, BarController, BarElement, LineController, LineElement,
  CategoryScale, LinearScale, PointElement, Tooltip, Legend
} from "chart.js";

Chart.register(
  BarController, BarElement,
  LineController, LineElement, PointElement,
  CategoryScale, LinearScale, Tooltip, Legend
);

const loading = ref(false);

const dateRange = ref([]);             // ["2025-01-01","2025-12-31"]
const groupBy = ref(["month"]);        // 기본 월별
const metric = ref("sum");
const chartType = ref("bar");

const rowData = ref([]);
const columnDefs = ref([]);
const defaultColDef = { sortable: true, filter: true, resizable: true };

const canvasRef = ref(null);
const chart = shallowRef(null);

const nf = new Intl.NumberFormat("ko-KR", { maximumFractionDigits: 0 });
const formatInt = (v) => (v == null ? "-" : nf.format(v));
const METRIC_LABEL = { sum: "합계", avg: "평균", max: "최대", min: "최소", count: "건수" };

// 차트 색 팔레트 (부서/계열 구분용)
const PALETTE = [
  "#5B8FF9", "#5AD8A6", "#F6BD16", "#E8684A", "#6DC8EC",
  "#9270CA", "#FF9D4D", "#269A99", "#FF99C3", "#5D7092",
];
const hexA = (hex, a) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

// 백엔드 columns([{key,label}]) → 그리드 컬럼 (금액은 정수 포맷)
function buildColumnsFromBackend(columns) {
  columnDefs.value = (columns || []).map((c) => ({
    field: c.key,
    headerName: c.label,
    sortable: true,
    filter: true,
    resizable: true,
    flex: c.key === "value" ? 0 : 1,
    minWidth: c.key === "value" ? 150 : 120,
    type: c.key === "value" ? "rightAligned" : undefined,
    valueFormatter: c.key === "value" ? (p) => formatInt(p.value) : undefined,
  }));
}

function destroyChart() {
  if (chart.value) {
    chart.value.destroy();
    chart.value = null;
  }
}

// 1차원=단일 시리즈, 2차원=grouped(계열=dim2)
function updateChartFromPivot(rows, dimCount) {
  destroyChart();
  if (!canvasRef.value || !rows.length) return;

  const isLine = chartType.value === "line";
  let labels, datasets;
  if (dimCount >= 2) {
    const xs = [...new Set(rows.map((r) => r.dim1))];
    const series = [...new Set(rows.map((r) => r.dim2))];
    const lookup = {};
    rows.forEach((r) => { lookup[r.dim1 + "~@@~" + r.dim2] = r.value; });
    labels = xs;
    datasets = series.map((s, i) => {
      const c = PALETTE[i % PALETTE.length];
      return {
        label: String(s),
        data: xs.map((x) => lookup[x + "~@@~" + s] ?? 0),
        backgroundColor: isLine ? hexA(c, 0.15) : c,
        borderColor: c,
        borderWidth: isLine ? 2 : 0,
        borderRadius: 5,
        maxBarThickness: 40,
        tension: 0.35,
        fill: false,
        pointRadius: isLine ? 2 : 0,
      };
    });
  } else {
    labels = rows.map((r) => r.dim1);
    const single = PALETTE[0];
    datasets = [{
      label: METRIC_LABEL[metric.value] || metric.value,
      data: rows.map((r) => r.value),
      backgroundColor: isLine ? hexA(single, 0.15) : labels.map((_, i) => PALETTE[i % PALETTE.length]),
      borderColor: single,
      borderWidth: isLine ? 2 : 0,
      borderRadius: 6,
      maxBarThickness: 56,
      tension: 0.35,
      fill: isLine,
      pointRadius: isLine ? 3 : 0,
    }];
  }

  const ctx = canvasRef.value.getContext("2d");
  chart.value = new Chart(ctx, {
    type: chartType.value,
    data: { labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: dimCount >= 2,
          position: "top",
          labels: { usePointStyle: true, boxWidth: 8, padding: 14 },
        },
        tooltip: {
          callbacks: { label: (c) => `${c.dataset.label}: ${nf.format(c.parsed.y)}` },
        },
      },
      scales: {
        x: { grid: { display: false }, ticks: { maxRotation: 60, minRotation: 60 } },
        y: { beginAtZero: true, grid: { color: "#f0f2f5" }, ticks: { callback: (v) => nf.format(v) } },
      },
    },
  });
}

// 마지막 조회 기간(드릴다운에서 재사용)
const lastQuery = ref({ start_date: null, end_date: null });

async function fetchPivot() {
  if (!groupBy.value.length) {
    ElMessage.warning("그룹 기준을 1개 이상 선택하세요.");
    return;
  }
  loading.value = true;
  try {
    const params = {
      group_by: groupBy.value.join(","),  // 콤마 문자열로 전송 (배열 직렬화 모호성 제거)
      metric: metric.value,
      metric_field: "amount",
    };
    if (dateRange.value?.length === 2) {
      params.start_date = dateRange.value[0];
      params.end_date = dateRange.value[1];
    }
    lastQuery.value = { start_date: params.start_date || null, end_date: params.end_date || null };

    const res = await axiosInstance.get("expense-pivot/", { params });
    const data = res.data || {};
    const rows = Array.isArray(data.rows) ? data.rows : [];
    const dimCount = Array.isArray(data.group_by) ? data.group_by.length : groupBy.value.length;

    buildColumnsFromBackend(data.columns);
    rowData.value = rows;

    if (rows.length) updateChartFromPivot(rows, dimCount);
    else destroyChart();
  } catch (e) {
    ElMessage.error("조회 실패: " + (e?.response?.data?.detail || e.message));
  } finally {
    loading.value = false;
  }
}

// ===== 드릴다운(집계 → 상세) =====
const showDetail = ref(false);
const detailLoading = ref(false);
const detailRows = ref([]);
const detailCount = ref(0);
const detailTotal = ref(0);
const detailTitle = ref("");

const detailColumns = [
  { field: "document_number", headerName: "문서번호", minWidth: 150 },
  { field: "created_at", headerName: "작성일", minWidth: 110 },
  { field: "payment_due_date", headerName: "결제요청일", minWidth: 120 },
  { field: "department", headerName: "부서", minWidth: 120 },
  { field: "partner", headerName: "거래처", minWidth: 140 },
  { field: "author", headerName: "작성자", minWidth: 110 },
  { field: "amount", headerName: "금액", minWidth: 130, type: "rightAligned",
    valueFormatter: (p) => formatInt(p.value) },
  { field: "status", headerName: "상태", minWidth: 110 },
  { field: "vat_type", headerName: "과세", minWidth: 90 },
];

async function onRowClicked(ev) {
  const row = ev?.data;
  if (!row || !row.drill) return;
  detailLoading.value = true;
  showDetail.value = true;
  const labels = [row.dim1, row.dim2].filter((x) => x != null);
  detailTitle.value = "상세 내역 — " + labels.join(" / ");
  try {
    const params = { detail: 1 };
    if (lastQuery.value.start_date) params.start_date = lastQuery.value.start_date;
    if (lastQuery.value.end_date) params.end_date = lastQuery.value.end_date;
    Object.entries(row.drill).forEach(([k, v]) => {
      if (v != null && v !== "") params["dl_" + k] = v;
    });
    const res = await axiosInstance.get("expense-pivot/", { params });
    const data = res.data || {};
    detailRows.value = Array.isArray(data.rows) ? data.rows : [];
    detailCount.value = data.count ?? detailRows.value.length;
    detailTotal.value = data.total ?? 0;
  } catch (e) {
    ElMessage.error("상세 조회 실패: " + (e?.response?.data?.detail || e.message));
  } finally {
    detailLoading.value = false;
  }
}

onMounted(fetchPivot);
onBeforeUnmount(() => destroyChart());
</script>

<style scoped>
.report-page { padding: 4px 2px; }

/* 헤더 */
.page-header { display: block; margin-bottom: 16px; }
.page-sub { font-size: 13px; color: #909399; margin: 4px 0 0; }

/* 공통 카드 */
.card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

/* 툴바 */
.toolbar {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  padding: 12px 14px;
  margin-bottom: 16px;
}

/* 표 + 차트 패널 */
.grid-chart-container { display: flex; gap: 16px; flex-wrap: wrap; align-items: stretch; }
.panel { flex: 1 1 480px; min-width: 340px; padding: 14px; display: flex; flex-direction: column; }
.panel-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 12px;
}
.panel-title { font-size: 14px; font-weight: 700; color: #1f2329; }
.panel-hint { font-size: 12px; color: #909399; }

.report-grid { height: 380px; width: 100%; }
.chart-box { position: relative; height: 380px; }

.detail-summary { font-size: 13px; font-weight: 600; margin-bottom: 10px; }

/* ag-grid 정돈: 헤더 강조 + 행 스트라이프 */
.report-grid :deep(.ag-header) { font-weight: 700; }
.report-grid :deep(.ag-row-odd) { background: #fafbfc; }
.report-grid :deep(.ag-row:hover) { background: #eef3fe; cursor: pointer; }

@media (max-width: 768px) {
  .panel { flex-basis: 100%; min-width: 0; }
  .toolbar :deep(.el-date-editor),
  .toolbar :deep(.el-select) { width: 100% !important; }
}
</style>
